(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const easeDuration = reducedMotion ? 0 : 180;
  let lastFocused = null;

  const setBodyLock = () => {
    const overlayOpen = $('[data-mobile-nav][aria-hidden="false"], [data-search-panel][aria-hidden="false"], #CartDrawer[aria-hidden="false"], [data-welcome-offer][aria-hidden="false"], .image-zoom-modal:not([aria-hidden="true"])');
    document.body.classList.toggle('no-scroll', Boolean(overlayOpen));
  };

  const setupReveal = (root = document) => {
    const groups = [
      '.section-heading', '.category-grid > a', '.product-grid > .product-card',
      '.story-grid > *', '.trust > .shell > div', '.newsletter > .shell > *',
      '.tech-grid > div', '.specs-layout > *', '.purchase-panel',
      '.amplifier-launch__copy', '.amplifier-launch__metric'
    ];
    groups.forEach(selector => $$(selector, root).forEach((element, index) => {
      if (element.hasAttribute('data-reveal')) return;
      element.setAttribute('data-reveal', 'up');
      element.style.setProperty('--reveal-delay', `${Math.min(index * 70, 420)}ms`);
    }));
    $$('.feature-story__media, .product-gallery', root).forEach(element => element.setAttribute('data-reveal', 'media'));
    $$('.feature-story__copy', root).forEach(element => element.setAttribute('data-reveal', 'up'));

    const items = $$('[data-reveal]:not([data-reveal-bound])', root);
    items.forEach(element => element.setAttribute('data-reveal-bound', ''));
    if (reducedMotion || !('IntersectionObserver' in window)) {
      items.forEach(element => element.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    }), { threshold: 0.08, rootMargin: '0px 0px -18% 0px' });
    items.forEach(element => observer.observe(element));
  };

  document.body.classList.add('motion-ready');
  setupReveal();
  document.addEventListener('shopify:section:load', event => setupReveal(event.target));

  const header = $('[data-header]');
  let previousY = window.scrollY;
  let headerFrame = 0;
  const updateHeader = () => {
    const y = window.scrollY;
    header?.classList.toggle('is-scrolled', y > 24);
    header?.classList.toggle('is-hidden', y > previousY && y > 140);
    previousY = y;
    headerFrame = 0;
  };
  updateHeader();
  window.addEventListener('scroll', () => {
    if (!headerFrame) headerFrame = requestAnimationFrame(updateHeader);
  }, { passive: true });

  const announcementItems = $$('[data-announcement] span');
  let announcementIndex = 0;
  announcementItems[0]?.classList.add('is-active');
  if (!reducedMotion && announcementItems.length > 1) setInterval(() => {
    announcementItems[announcementIndex].classList.remove('is-active');
    announcementIndex = (announcementIndex + 1) % announcementItems.length;
    announcementItems[announcementIndex].classList.add('is-active');
  }, 3400);

  const mobileNav = $('[data-mobile-nav]');
  const menuButtons = $$('[data-menu-toggle]');
  const setMenu = open => {
    mobileNav?.setAttribute('aria-hidden', String(!open));
    menuButtons.forEach(button => button.setAttribute('aria-expanded', String(open)));
    setBodyLock();
    if (open) {
      lastFocused = document.activeElement;
      setTimeout(() => $('a', mobileNav)?.focus(), easeDuration);
    } else if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };
  menuButtons.forEach(button => button.addEventListener('click', () => setMenu(mobileNav?.getAttribute('aria-hidden') === 'true')));

  const searchPanel = $('[data-search-panel]');
  const searchButtons = $$('[data-search-toggle]');
  const setSearch = open => {
    searchPanel?.setAttribute('aria-hidden', String(!open));
    searchButtons.forEach(button => button.setAttribute('aria-expanded', String(open)));
    setBodyLock();
    if (open) {
      lastFocused = document.activeElement;
      setTimeout(() => $('input', searchPanel)?.focus(), easeDuration);
    } else if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };
  searchButtons.forEach(button => button.addEventListener('click', () => setSearch(searchPanel?.getAttribute('aria-hidden') === 'true')));

  const intro = $('[data-site-intro]');
  const introPending = document.documentElement.classList.contains('intro-pending');
  let introComplete = false;
  const finishIntro = () => {
    if (introComplete) return;
    introComplete = true;
    document.documentElement.classList.remove('intro-pending');
    document.documentElement.classList.add('intro-complete');
    intro?.classList.add('is-complete');
    try { sessionStorage.setItem('aerix-intro-seen', '1'); } catch (error) { /* session storage can be unavailable */ }
    document.dispatchEvent(new CustomEvent('aerix:intro-complete'));
    window.removeEventListener('wheel', finishIntro);
    window.removeEventListener('touchstart', finishIntro);
    window.removeEventListener('keydown', finishIntro);
    setTimeout(() => intro?.remove(), 650);
  };
  if (introPending && intro && !reducedMotion) {
    requestAnimationFrame(() => intro.classList.add('is-running'));
    intro.addEventListener('pointerdown', finishIntro, { once: true });
    window.addEventListener('wheel', finishIntro, { passive: true, once: true });
    window.addEventListener('touchstart', finishIntro, { passive: true, once: true });
    window.addEventListener('keydown', finishIntro, { once: true });
    setTimeout(finishIntro, window.innerWidth <= 760 ? 2050 : 2500);
  } else {
    finishIntro();
  }

  const setupHeroCarousel = carousel => {
    if (!carousel || carousel.hasAttribute('data-hero-bound')) return;
    carousel.setAttribute('data-hero-bound', '');
    const slides = $$('[data-hero-slide]', carousel);
    const goButtons = $$('[data-hero-go]', carousel);
    const previousButton = $('[data-hero-prev]', carousel);
    const nextButton = $('[data-hero-next]', carousel);
    const currentLabel = $('[data-hero-current]', carousel);
    const duration = Math.max(5000, Number(carousel.dataset.autoplay) || 6000);
    carousel.style.setProperty('--hero-autoplay', `${duration}ms`);
    let active = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
    let transitionTimer = 0;
    let autoplayTimer = 0;
    let resumeTimer = 0;
    let transitioning = false;
    let hovered = false;
    let touchX = 0;
    let touchY = 0;

    const playActiveVideo = () => {
      slides.forEach((slide, index) => {
        const video = $('[data-hero-video]', slide);
        if (!video) return;
        if (index === active && window.innerWidth > 760) video.play().catch(() => {});
        else video.pause();
      });
    };

    const resetProgress = () => {
      carousel.classList.remove('is-timing');
      void carousel.offsetWidth;
      if (!reducedMotion && slides.length > 1) carousel.classList.add('is-timing');
    };

    const syncUi = () => {
      goButtons.forEach((button, index) => {
        button.classList.toggle('is-active', index === active);
        button.setAttribute('aria-current', String(index === active));
      });
      if (currentLabel) currentLabel.textContent = String(active + 1).padStart(2, '0');
      resetProgress();
      playActiveVideo();
    };

    const clearAutoplay = () => {
      clearTimeout(autoplayTimer);
      carousel.classList.add('is-paused');
    };

    const scheduleAutoplay = () => {
      clearTimeout(autoplayTimer);
      if (reducedMotion || slides.length < 2 || hovered || document.hidden) return;
      carousel.classList.remove('is-paused');
      resetProgress();
      autoplayTimer = setTimeout(() => goTo((active + 1) % slides.length), duration);
    };

    const pauseAfterInteraction = () => {
      clearAutoplay();
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(scheduleAutoplay, 10000);
    };

    const goTo = (nextIndex, userInitiated = false) => {
      if (transitioning || slides.length < 2) return;
      const next = (nextIndex + slides.length) % slides.length;
      if (next === active) {
        if (userInitiated) pauseAfterInteraction();
        return;
      }
      transitioning = true;
      clearTimeout(autoplayTimer);
      clearTimeout(transitionTimer);
      const currentSlide = slides[active];
      const nextSlide = slides[next];
      currentSlide.classList.add('is-leaving');
      currentSlide.setAttribute('aria-hidden', 'true');
      nextSlide.classList.add('is-active', 'is-entering');
      nextSlide.setAttribute('aria-hidden', 'false');
      active = next;
      syncUi();
      transitionTimer = setTimeout(() => {
        slides.forEach((slide, index) => {
          if (index !== active) slide.classList.remove('is-active');
          slide.classList.remove('is-entering', 'is-leaving');
        });
        transitioning = false;
        if (userInitiated) pauseAfterInteraction();
        else scheduleAutoplay();
      }, reducedMotion ? 40 : 820);
    };

    previousButton?.addEventListener('click', () => goTo(active - 1, true));
    nextButton?.addEventListener('click', () => goTo(active + 1, true));
    goButtons.forEach(button => button.addEventListener('click', () => goTo(Number(button.dataset.heroGo), true)));
    carousel.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); goTo(active - 1, true); }
      if (event.key === 'ArrowRight') { event.preventDefault(); goTo(active + 1, true); }
    });
    carousel.addEventListener('mouseenter', () => { hovered = true; clearAutoplay(); });
    carousel.addEventListener('mouseleave', () => { hovered = false; scheduleAutoplay(); });
    carousel.addEventListener('touchstart', event => {
      touchX = event.changedTouches[0].clientX;
      touchY = event.changedTouches[0].clientY;
      clearAutoplay();
    }, { passive: true });
    carousel.addEventListener('touchend', event => {
      const deltaX = event.changedTouches[0].clientX - touchX;
      const deltaY = event.changedTouches[0].clientY - touchY;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) goTo(active + (deltaX < 0 ? 1 : -1), true);
      else pauseAfterInteraction();
    }, { passive: true });

    if (finePointer && !reducedMotion && window.innerWidth >= 1024) {
      let pointerFrame = 0;
      let pointerEvent;
      carousel.addEventListener('pointermove', event => {
        pointerEvent = event;
        if (pointerFrame) return;
        pointerFrame = requestAnimationFrame(() => {
          const rect = carousel.getBoundingClientRect();
          const x = (pointerEvent.clientX - rect.left) / rect.width;
          const y = (pointerEvent.clientY - rect.top) / rect.height;
          carousel.style.setProperty('--product-x', `${(x - .5) * 14}px`);
          carousel.style.setProperty('--product-y', `${(y - .5) * 10}px`);
          carousel.style.setProperty('--foreground-x', `${(.5 - x) * 28}px`);
          carousel.style.setProperty('--foreground-y', `${(.5 - y) * 20}px`);
          carousel.style.setProperty('--background-x', `${(x - .5) * 6}px`);
          carousel.style.setProperty('--background-y', `${(y - .5) * 5}px`);
          carousel.style.setProperty('--light-x', `${x * 100}%`);
          carousel.style.setProperty('--light-y', `${y * 100}%`);
          pointerFrame = 0;
        });
      }, { passive: true });
      carousel.addEventListener('pointerleave', () => {
        carousel.style.setProperty('--product-x', '0px');
        carousel.style.setProperty('--product-y', '0px');
        carousel.style.setProperty('--foreground-x', '0px');
        carousel.style.setProperty('--foreground-y', '0px');
        carousel.style.setProperty('--background-x', '0px');
        carousel.style.setProperty('--background-y', '0px');
        carousel.style.setProperty('--light-x', '68%');
        carousel.style.setProperty('--light-y', '45%');
      });
    }

    let scrollFrame = 0;
    const updateHeroDepth = () => {
      const progress = Math.min(1, Math.max(0, window.scrollY / Math.max(carousel.offsetHeight, 1)));
      carousel.style.setProperty('--hero-depth-scale', String(1 - progress * .04));
      carousel.style.setProperty('--hero-depth-y', `${progress * 30}px`);
      carousel.style.setProperty('--hero-text-y', `${progress * 44}px`);
      carousel.style.setProperty('--hero-depth-opacity', String(1 - progress * .25));
      scrollFrame = 0;
    };
    window.addEventListener('scroll', () => {
      if (!scrollFrame && window.scrollY < carousel.offsetHeight * 1.2) scrollFrame = requestAnimationFrame(updateHeroDepth);
    }, { passive: true });
    document.addEventListener('visibilitychange', () => document.hidden ? clearAutoplay() : scheduleAutoplay());
    updateHeroDepth();
    syncUi();
    if (introPending && !introComplete) document.addEventListener('aerix:intro-complete', scheduleAutoplay, { once: true });
    else scheduleAutoplay();
  };

  setupHeroCarousel($('[data-hero-carousel]'));
  document.addEventListener('shopify:section:load', event => setupHeroCarousel($('[data-hero-carousel]', event.target)));

  if (finePointer && !reducedMotion) {
    $$('[data-magnetic]').forEach(button => {
      button.addEventListener('pointermove', event => {
        const rect = button.getBoundingClientRect();
        button.style.setProperty('--magnetic-x', `${((event.clientX - rect.left) / rect.width - .5) * 8}px`);
        button.style.setProperty('--magnetic-y', `${((event.clientY - rect.top) / rect.height - .5) * 6}px`);
      }, { passive: true });
      button.addEventListener('pointerleave', () => {
        button.style.setProperty('--magnetic-x', '0px');
        button.style.setProperty('--magnetic-y', '0px');
      });
    });

    $$('.template-index [data-depth-card]').forEach(card => {
      let cardFrame = 0;
      let cardEvent;
      card.addEventListener('pointermove', event => {
        cardEvent = event;
        if (cardFrame) return;
        cardFrame = requestAnimationFrame(() => {
          const rect = card.getBoundingClientRect();
          const x = (cardEvent.clientX - rect.left) / rect.width;
          const y = (cardEvent.clientY - rect.top) / rect.height;
          card.style.setProperty('--card-rx', `${(.5 - y) * 3}deg`);
          card.style.setProperty('--card-ry', `${(x - .5) * 4}deg`);
          card.style.setProperty('--card-x', `${x * 100}%`);
          card.style.setProperty('--card-y', `${y * 100}%`);
          cardFrame = 0;
        });
      }, { passive: true });
      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--card-rx', '0deg');
        card.style.setProperty('--card-ry', '0deg');
      });
    });
  }

  $$('[data-gallery]').forEach(gallery => {
    const slides = $$('[data-gallery-slide]', gallery);
    const thumbs = $$('[data-gallery-thumb]', gallery);
    const main = $('.product-gallery__main', gallery);
    const currentLabel = $('[data-gallery-current]', gallery);
    const progress = $('[data-gallery-progress]', gallery);
    let active = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
    let touchX = 0;
    let touchY = 0;
    const show = index => {
      if (!slides.length) return;
      active = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === active;
        slide.classList.toggle('is-active', selected);
        slide.setAttribute('aria-hidden', String(!selected));
      });
      thumbs.forEach((thumb, thumbIndex) => {
        const selected = thumbIndex === active;
        thumb.classList.toggle('is-active', selected);
        thumb.setAttribute('aria-current', String(selected));
      });
      if (currentLabel) currentLabel.textContent = String(active + 1);
      if (progress) progress.style.width = `${(active + 1) / slides.length * 100}%`;
    };
    thumbs.forEach(button => button.addEventListener('click', () => show(Number(button.dataset.galleryThumb))));
    $('[data-gallery-prev]', gallery)?.addEventListener('click', () => show(active - 1));
    $('[data-gallery-next]', gallery)?.addEventListener('click', () => show(active + 1));
    main?.addEventListener('keydown', event => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(active - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(active + 1); }
    });
    main?.addEventListener('touchstart', event => {
      touchX = event.changedTouches[0].clientX;
      touchY = event.changedTouches[0].clientY;
    }, { passive: true });
    main?.addEventListener('touchend', event => {
      const deltaX = event.changedTouches[0].clientX - touchX;
      const deltaY = event.changedTouches[0].clientY - touchY;
      if (Math.abs(deltaX) > 44 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) show(active + (deltaX < 0 ? 1 : -1));
    }, { passive: true });
    show(active);
  });

  const updateVariant = () => {
    const select = $('[data-variant-select]');
    if (!select) return;
    const selected = $$('[name^="option-"]:checked').map(input => input.value).join('||');
    const option = [...select.options].find(item => item.dataset.options === selected);
    if (option) {
      select.value = option.value;
      $('[data-price]').textContent = option.dataset.price;
      $('[data-sticky-price]')?.replaceChildren(option.dataset.price);
    }
  };
  $$('[name^="option-"]').forEach(input => input.addEventListener('change', updateVariant));

  const drawer = $('#CartDrawer');
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  const renderCart = async () => {
    const cart = await fetch('/cart.js').then(response => response.json());
    $$('[data-cart-count]').forEach(item => item.textContent = cart.item_count);
    const content = $('#CartDrawerContent');
    if (!content) return;
    if (!cart.items.length) {
      content.innerHTML = '<div class="drawer-empty"><h3>Your cart is empty.</h3><a class="button button--primary" href="/collections/all">Shop products</a></div>';
      return;
    }
    const threshold = Number(document.documentElement.dataset.shippingThreshold || 49) * 100;
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = threshold ? Math.min(100, cart.total_price / threshold * 100) : 100;
    content.innerHTML = `<div class="shipping-progress${remaining ? '' : ' is-unlocked'}"><p>${remaining ? `${money(remaining)} away from free shipping.` : 'FREE SHIPPING UNLOCKED'}</p><i><b data-progress-bar></b></i></div>${cart.items.map(item => `<div class="drawer-line"><img src="${item.image || ''}" alt=""><span><b>${item.product_title}</b><small>${item.variant_title || ''}</small><a href="/cart/change?id=${item.key}&quantity=0" data-remove-key="${item.key}">Remove</a></span><strong>${money(item.final_line_price)}</strong></div>`).join('')}<div class="drawer-total"><p><span>Subtotal</span><b>${money(cart.total_price)}</b></p><a class="drawer-welcome" href="/discount/WELCOME20?redirect=%2Fcart"><span>WELCOME20</span><b>20% OFF FIRST ORDER ↗</b></a><a class="button button--primary" href="/checkout">Checkout →</a><a href="/cart">View cart</a></div>`;
    requestAnimationFrame(() => $('[data-progress-bar]', content)?.style.setProperty('width', `${progress}%`));
  };
  const openDrawer = async () => {
    lastFocused = document.activeElement;
    await renderCart();
    drawer?.setAttribute('aria-hidden', 'false');
    setBodyLock();
    setTimeout(() => $('[data-drawer-close]', drawer)?.focus(), easeDuration);
  };
  const closeDrawer = () => {
    drawer?.setAttribute('aria-hidden', 'true');
    setBodyLock();
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  };
  $$('[data-cart-open]').forEach(button => button.addEventListener('click', openDrawer));
  $$('[data-drawer-close]').forEach(button => button.addEventListener('click', closeDrawer));
  $('#CartDrawerContent')?.addEventListener('click', async event => {
    const link = event.target.closest('[data-remove-key]');
    if (!link) return;
    event.preventDefault();
    const line = link.closest('.drawer-line');
    line?.classList.add('is-removing');
    await new Promise(resolve => setTimeout(resolve, easeDuration));
    await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: link.dataset.removeKey, quantity: 0 }) });
    await renderCart();
  });

  $$('[data-product-form]').forEach(form => form.addEventListener('submit', async event => {
    event.preventDefault();
    const button = $('button[type="submit"]', form);
    const originalLabel = button?.dataset.label || button?.textContent || 'ADD TO CART';
    if (button) {
      button.dataset.label = originalLabel;
      button.disabled = true;
      button.textContent = 'ADDING…';
    }
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error('Cart request failed');
      if (button) button.textContent = '✓ ADDED';
      $$('.cart-icon').forEach(icon => {
        icon.classList.remove('is-pulsing');
        requestAnimationFrame(() => icon.classList.add('is-pulsing'));
      });
      await openDrawer();
    } catch (error) {
      if (button) button.textContent = 'TRY AGAIN';
    } finally {
      setTimeout(() => {
        if (!button) return;
        button.disabled = false;
        button.textContent = originalLabel;
      }, 1200);
    }
  }));

  const productForm = $('#ProductForm');
  const stickyBuy = $('[data-sticky-buy]');
  if (productForm && stickyBuy && 'IntersectionObserver' in window) {
    const stickyObserver = new IntersectionObserver(entries => {
      const show = !entries[0].isIntersecting && window.scrollY > productForm.getBoundingClientRect().height;
      stickyBuy.classList.toggle('is-visible', show);
      stickyBuy.setAttribute('aria-hidden', String(!show));
    }, { threshold: .08 });
    stickyObserver.observe(productForm);
    $('[data-sticky-add]')?.addEventListener('click', () => productForm.requestSubmit());
  }

  const zoomImages = $$('[data-zoom-gallery] img');
  if (zoomImages.length) {
    const modal = document.createElement('div');
    modal.className = 'image-zoom-modal';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = '<button type="button" aria-label="Close image">×</button><img alt="">';
    document.body.appendChild(modal);
    const closeZoom = () => {
      modal.setAttribute('aria-hidden', 'true');
      setBodyLock();
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    };
    zoomImages.forEach(image => image.addEventListener('click', () => {
      lastFocused = image;
      $('img', modal).src = image.currentSrc || image.src;
      $('img', modal).alt = image.alt;
      modal.setAttribute('aria-hidden', 'false');
      setBodyLock();
      $('button', modal).focus();
    }));
    $('button', modal).addEventListener('click', closeZoom);
    modal.addEventListener('click', event => { if (event.target === modal) closeZoom(); });
  }

  if (!reducedMotion && 'IntersectionObserver' in window) {
    const numberObserver = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const element = entry.target;
      const match = element.textContent.trim().match(/^(\d+)(\s*(?:W|H|CH|M|PCS|K\+))$/i);
      numberObserver.unobserve(element);
      if (!match) return;
      const target = Number(match[1]);
      const suffix = match[2];
      const start = performance.now();
      const tick = now => {
        const progress = Math.min(1, (now - start) / 900);
        element.textContent = `${Math.round(target * (1 - Math.pow(1 - progress, 3)))}${suffix}`;
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }), { threshold: .5 });
    $$('.tech-grid span').forEach(element => numberObserver.observe(element));
  }

  const welcomeOffer = $('[data-welcome-offer]');
  if (welcomeOffer && !window.Shopify?.designMode) {
    const offerPanel = $('.welcome-offer__panel', welcomeOffer);
    const storageKey = 'aerix-welcome-dismissed-at';
    const suppressionWindow = 7 * 24 * 60 * 60 * 1000;
    const rememberOffer = () => {
      try { localStorage.setItem(storageKey, String(Date.now())); } catch (error) { /* storage can be unavailable */ }
    };
    const closeWelcomeOffer = () => {
      if (welcomeOffer.getAttribute('aria-hidden') === 'true') return;
      welcomeOffer.setAttribute('aria-hidden', 'true');
      rememberOffer();
      setBodyLock();
      if (lastFocused instanceof HTMLElement) lastFocused.focus();
    };
    const showWelcomeOffer = () => {
      lastFocused = document.activeElement;
      welcomeOffer.setAttribute('aria-hidden', 'false');
      setBodyLock();
      setTimeout(() => offerPanel?.focus(), easeDuration);
    };
    const forceOffer = new URLSearchParams(window.location.search).get('show_offer') === '1';
    let suppressed = false;
    try {
      const dismissedAt = Number(localStorage.getItem(storageKey) || 0);
      suppressed = dismissedAt > 0 && Date.now() - dismissedAt < suppressionWindow;
    } catch (error) { /* storage can be unavailable */ }
    if (forceOffer) suppressed = false;
    if (!suppressed) setTimeout(showWelcomeOffer, forceOffer ? 350 : Number(welcomeOffer.dataset.delay || 6000));
    $$('[data-welcome-close]', welcomeOffer).forEach(button => button.addEventListener('click', closeWelcomeOffer));
    $('[data-welcome-apply]', welcomeOffer)?.addEventListener('click', rememberOffer);
    $('[data-copy-code]', welcomeOffer)?.addEventListener('click', async event => {
      const button = event.currentTarget;
      const status = $('[data-copy-status]', button);
      try {
        await navigator.clipboard.writeText(button.dataset.code || 'WELCOME20');
        if (status) status.textContent = 'COPIED ✓';
      } catch (error) {
        if (status) status.textContent = 'CODE: WELCOME20';
      }
      rememberOffer();
      setTimeout(() => { if (status) status.textContent = 'COPY'; }, 1800);
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && welcomeOffer.getAttribute('aria-hidden') === 'false') closeWelcomeOffer();
    });
  }

  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (drawer?.getAttribute('aria-hidden') === 'false') closeDrawer();
    if (searchPanel?.getAttribute('aria-hidden') === 'false') setSearch(false);
    if (mobileNav?.getAttribute('aria-hidden') === 'false') setMenu(false);
    const zoom = $('.image-zoom-modal:not([aria-hidden="true"])');
    zoom?.querySelector('button')?.click();
  });

  $$('product-recommendations').forEach(element => {
    const observer = new IntersectionObserver(async entries => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const html = await fetch(element.dataset.url).then(response => response.text());
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('product-recommendations', parsed);
      if (fresh?.innerHTML.trim()) {
        element.innerHTML = fresh.innerHTML;
        setupReveal(element);
      }
    }, { rootMargin: '200px' });
    observer.observe(element);
  });
})();
